import { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, CheckCircle, AlertCircle, Sparkles, Brain, Star
} from '../components/icons';
import { supabase } from '../lib/supabase';

export function FreeProfileSignupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get('ref') || '';
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    coachingNiche: '',
    country: '',
    referralCode: refCode,
    termsAccepted: false,
    privacyAccepted: false,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (!formData.termsAccepted || !formData.privacyAccepted) {
      setError('Please accept the Terms and Privacy Policy to continue');
      return;
    }

    if (!formData.referralCode.trim()) {
      setError('An activation code is required to create a free coach account');
      return;
    }

    setIsLoading(true);

    try {
      const trimmedCode = formData.referralCode.trim();

      // Look up partner by free_profile_code (short code) OR by partner UUID
      // (the referral link falls back to the partner's UUID when no short code is set)
      const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(trimmedCode);

      let partner: { id: string; user_id: string } | null = null;
      let partnerError: any = null;

      if (isUuid) {
        const result = await supabase
          .from('partners')
          .select('id, user_id')
          .eq('id', trimmedCode)
          .maybeSingle();
        partner = result.data;
        partnerError = result.error;
      } else {
        const result = await supabase
          .from('partners')
          .select('id, user_id')
          .eq('free_profile_code', trimmedCode.toUpperCase())
          .maybeSingle();
        partner = result.data;
        partnerError = result.error;
      }

      if (partnerError) throw partnerError;
      if (!partner) {
        setError('The activation code you entered was not recognized. Please check it and try again.');
        return;
      }

      const validatedPartnerId = partner.id;

      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            full_name: `${formData.firstName} ${formData.lastName}`,
            role: 'coach',
          },
        },
      });

      if (signUpError || !authData.user) {
        throw signUpError || new Error('Failed to create account');
      }

      // Activate the session immediately so all subsequent DB inserts pass RLS
      if (authData.session) {
        await supabase.auth.setSession({
          access_token: authData.session.access_token,
          refresh_token: authData.session.refresh_token,
        });
      }

      const fullName = `${formData.firstName} ${formData.lastName}`;
      const assessmentCode = 'FP' + Array.from({ length: 6 }, () =>
        'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'[Math.floor(Math.random() * 36)]
      ).join('');

      const partnerId: string = validatedPartnerId;

      await supabase.from('users').insert({
        id: authData.user.id,
        email: formData.email,
        full_name: fullName,
        role: 'coach',
      });

      const founderExpiry = new Date();
      founderExpiry.setDate(founderExpiry.getDate() + 7);

      const { data: coachRecord } = await supabase.from('coaches').insert({
        user_id: authData.user.id,
        partner_id: partnerId,
        tier: 'basic',
        assessment_code: assessmentCode,
        ai_analysis_access: true,
        max_respondents: 3,
        profile_only_mode: true,
        account_plan: 'FREE_PROFILE_COACH',
        free_profile_referral_partner_id: partnerId,
        free_profile_referral_source: 'partner_referral',
        free_profile_complimentary_assessments: 3,
        free_profile_completed_assessments: 0,
        free_profile_founder_session_expiry: founderExpiry.toISOString(),
        free_profile_onboarding_completed: false,
        free_profile_upgrade_status: 'none',
        free_profile_founder_session_booked: false,
        free_profile_coaching_niche: formData.coachingNiche || null,
        free_profile_country: formData.country || null,
      }).select('id').single();

      if (coachRecord) {
        await supabase.from('free_profile_funnel_events').insert({
          coach_id: coachRecord.id,
          event_type: 'signup',
          event_metadata: { referral_code: formData.referralCode || null },
        });
      }

      await supabase.from('assessment_codes').insert({
        coach_id: coachRecord?.id,
        code: assessmentCode,
        is_used: false,
      });

      try {
        await supabase.functions.invoke('send-email', {
          body: {
            type: 'free_profile_account_created',
            recipientEmail: formData.email,
            recipientName: fullName,
            loginUrl: 'https://my.nlpos.app/login',
          },
        });
      } catch {
        // Email is best-effort
      }

      // Notify the referring partner that a new coach activated
      try {
        const { data: partnerUser } = await supabase
          .from('users')
          .select('email, full_name')
          .eq('id', partner.user_id)
          .maybeSingle();

        if (partnerUser?.email) {
          await supabase.functions.invoke('send-email', {
            body: {
              type: 'free_profile_coach_activated',
              recipientEmail: partnerUser.email,
              recipientName: partnerUser.full_name || '',
              coachName: fullName,
              coachEmail: formData.email,
              coachNiche: formData.coachingNiche || '',
              coachCountry: formData.country || '',
              signupDate: new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
              loginUrl: 'https://my.nlpos.app/login',
            },
          });
        }
      } catch {
        // Partner notification is best-effort
      }

      navigate('/coach/onboarding', { replace: true });
    } catch (err: any) {
      console.error('Free profile registration error:', err);
      if (err?.message?.includes('already registered')) {
        setError('An account with this email already exists');
      } else {
        setError(err?.message || 'Registration failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const heroBenefits = [
    'Attract prospective clients with a meaningful assessment',
    'Create deeper and more valuable coaching conversations',
    'Identify patterns, blind spots and breakthrough opportunities faster',
    'Demonstrate the value of your coaching before asking someone to buy',
    'Help clients feel understood from the beginning',
  ];

  const includedItems = [
    { icon: Star, label: '3 complimentary completed assessments' },
    { icon: Brain, label: 'AI-assisted analysis included' },
    { icon: CheckCircle, label: 'No credit card required' },
    { icon: Sparkles, label: 'Profile access only' },
  ];

  return (
    <div className="min-h-screen freud-bg flex flex-col">
      <div className="flex-1 py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-chocolate-600 hover:text-sage-600 mb-8 transition-colors font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Hero / Marketing Panel */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-gradient-to-br from-sage-600 to-sage-700 rounded-2xl p-8 text-white"
            >
              <h1 className="freud-heading-xl text-white mb-4 leading-tight">
                Give Your Prospects and Clients an Experience That Makes Them Feel Truly Understood
              </h1>

              <p className="freud-text-base text-sage-100 mb-8">
                Open your free Human Potential Profile coach account and use the Profile with up to three people at no cost.
              </p>

              <h3 className="freud-label text-white mb-4">Discover how the Profile can help you:</h3>
              <ul className="space-y-3 mb-8">
                {heroBenefits.map((benefit, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-sage-300 flex-shrink-0 mt-0.5" />
                    <span className="freud-text-sm text-sage-50">{benefit}</span>
                  </li>
                ))}
              </ul>

              <div className="space-y-3 mb-6">
                {includedItems.map((item, i) => (
                  <div key={i} className="flex items-center gap-3 bg-white/10 rounded-xl p-3">
                    <item.icon className="w-5 h-5 text-sage-200 flex-shrink-0" />
                    <span className="freud-text-sm text-white font-medium">{item.label}</span>
                  </div>
                ))}
              </div>

              <div className="p-4 bg-white/10 rounded-xl">
                <p className="freud-text-sm text-sage-50 leading-relaxed">
                  The Human Potential Profile is the Profile component of NLP OS. It helps coaches understand how a person is currently thinking, behaving and approaching their life, goals and challenges.
                </p>
                <p className="freud-text-sm text-sage-100 mt-2 font-medium">
                  Your free account includes Profile access only.
                </p>
              </div>
            </motion.div>

            {/* Registration Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="freud-card-elevated p-8"
            >
              <h2 className="freud-heading-lg text-chocolate-900 mb-2">Open My Free Coach Account</h2>
              <p className="freud-text-sm text-chocolate-500 mb-6">
                No credit card required. Get started in under 2 minutes.
              </p>

              {error && (
                <div className="flex items-center gap-2 bg-warm-100 text-warm-700 p-4 rounded-xl mb-6">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <p className="freud-text-sm">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="freud-label block text-chocolate-800 mb-2">First Name</label>
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      required
                      className="freud-input"
                      placeholder="Jane"
                    />
                  </div>
                  <div>
                    <label className="freud-label block text-chocolate-800 mb-2">Last Name</label>
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      required
                      className="freud-input"
                      placeholder="Smith"
                    />
                  </div>
                </div>

                <div>
                  <label className="freud-label block text-chocolate-800 mb-2">Email Address</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="freud-input"
                    placeholder="you@example.com"
                  />
                </div>

                <div>
                  <label className="freud-label block text-chocolate-800 mb-2">Password</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    minLength={8}
                    className="freud-input"
                    placeholder="Minimum 8 characters"
                  />
                </div>

                <div>
                  <label className="freud-label block text-chocolate-800 mb-2">
                    Coaching Niche or Area of Work
                  </label>
                  <input
                    type="text"
                    value={formData.coachingNiche}
                    onChange={(e) => setFormData({ ...formData, coachingNiche: e.target.value })}
                    className="freud-input"
                    placeholder="e.g. Executive coaching, life coaching"
                  />
                </div>

                <div>
                  <label className="freud-label block text-chocolate-800 mb-2">Country</label>
                  <input
                    type="text"
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    className="freud-input"
                    placeholder="e.g. United Kingdom"
                  />
                </div>

                <div>
                  <label className="freud-label block text-chocolate-800 mb-2">
                    Activation Code <span className="text-coral-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.referralCode}
                    onChange={(e) => setFormData({ ...formData, referralCode: e.target.value.toUpperCase() })}
                    className="freud-input font-mono tracking-widest"
                    placeholder="e.g. FPC12345"
                    required
                    maxLength={10}
                  />
                </div>

                <div className="space-y-3 pt-2">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.termsAccepted}
                      onChange={(e) => setFormData({ ...formData, termsAccepted: e.target.checked })}
                      className="mt-1 w-4 h-4 rounded border-chocolate-300 text-sage-600 focus:ring-sage-500"
                    />
                    <span className="freud-text-sm text-chocolate-600">
                      I accept the{' '}
                      <Link to="/terms" className="text-sage-600 hover:text-sage-700 font-medium" target="_blank">
                        Terms of Service
                      </Link>
                    </span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.privacyAccepted}
                      onChange={(e) => setFormData({ ...formData, privacyAccepted: e.target.checked })}
                      className="mt-1 w-4 h-4 rounded border-chocolate-300 text-sage-600 focus:ring-sage-500"
                    />
                    <span className="freud-text-sm text-chocolate-600">
                      I accept the{' '}
                      <Link to="/privacy" className="text-sage-600 hover:text-sage-700 font-medium" target="_blank">
                        Privacy Policy
                      </Link>
                    </span>
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="freud-btn-primary w-full"
                >
                  {isLoading ? 'Creating Your Free Account...' : 'Open My Free Coach Account'}
                </button>
              </form>

              <p className="mt-6 text-center freud-text-sm text-chocolate-500">
                Already have an account?{' '}
                <Link to="/login" className="text-coral-600 hover:text-coral-700 font-medium">
                  Sign in
                </Link>
              </p>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-6 px-4 border-t border-chocolate-100">
        <p className="text-center text-xs text-chocolate-400">
          &copy; {new Date().getFullYear()} NLP OS. NLP OS&trade; and Human Potential Profile&trade; are trademarks of NLP OS.
        </p>
      </footer>
    </div>
  );
}
